import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatRupiah, formatDateTime, STAGE_META } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Users, TrendingUp, DollarSign, Trophy, XCircle,
  Activity as ActIcon, Clock, AlertTriangle, ArrowUpRight, Plus,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line,
  PieChart, Pie, Cell, CartesianGrid, Legend,
} from "recharts";

const CHART_COLORS = ["#064E3B","#059669","#10B981","#34D399","#6EE7B7","#A7F3D0","#F59E0B","#EF4444"];

function KPI({ icon: Icon, label, value, sub, tint = "emerald", onClick, tid }) {
  return (
    <div onClick={onClick} className="kpi-card cursor-pointer" data-testid={tid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-slate-500 font-medium uppercase tracking-wide">{label}</div>
          <div className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Outfit" }}>{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-${tint}-50 text-${tint}-700`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [followups, setFollowups] = useState([]);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setD(r.data));
    api.get("/followups", { params: { status: "today" } }).then((r) => setFollowups(r.data));
  }, []);

  if (!d) return <div className="text-slate-500">Memuat...</div>;

  const stageChart = d.deals_by_stage.map((s) => ({ name: STAGE_META[s.stage]?.id || s.stage, count: s.count }));
  const sources = d.lead_sources;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
            {t("greeting")}, {user?.full_name?.split(" ")[0]} <span>👋</span>
          </h1>
          <p className="text-sm text-slate-500 mt-1">Berikut ringkasan performa penjualan Anda.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => nav("/leads")} className="brand-btn" data-testid="quick-add-lead"><Plus className="w-4 h-4 mr-1"/>{t("action.newLead")}</Button>
          <Button onClick={() => nav("/deals")} variant="outline" data-testid="quick-add-deal"><Plus className="w-4 h-4 mr-1"/>{t("action.newDeal")}</Button>
          <Button onClick={() => nav("/followup")} variant="outline" data-testid="quick-add-followup"><Plus className="w-4 h-4 mr-1"/>{t("action.newFollowup")}</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={Users}       label={t("kpi.totalLeads")}    value={d.total_leads}    sub={`+${d.new_leads} baru`} onClick={()=>nav("/leads")} tid="kpi-total-leads" />
        <KPI icon={TrendingUp}  label={t("kpi.activeDeals")}   value={d.active_deals}   sub={`${d.won_deals} menang · ${d.lost_deals} kalah`} onClick={()=>nav("/deals")} tid="kpi-active-deals" />
        <KPI icon={DollarSign}  label={t("kpi.pipelineValue")} value={formatRupiah(d.pipeline_value)} sub={`W ${formatRupiah(d.weighted_pipeline)}`} onClick={()=>nav("/deals")} tid="kpi-pipeline-value" />
        <KPI icon={Trophy}      label={t("kpi.revenue")}       value={formatRupiah(d.revenue)} sub={`${d.won_deals} won deals`} onClick={()=>nav("/reports")} tid="kpi-revenue" />
        <KPI icon={ActIcon}     label={t("kpi.newLeads")}      value={d.new_leads}      onClick={()=>nav("/leads")} tid="kpi-new-leads" />
        <KPI icon={Clock}       label={t("kpi.followupToday")} value={d.followup_today} onClick={()=>nav("/followup")} tid="kpi-followup-today" tint="amber" />
        <KPI icon={AlertTriangle} label={t("kpi.overdue")}     value={d.followup_overdue} onClick={()=>nav("/followup")} tid="kpi-followup-overdue" tint="rose" />
        <KPI icon={XCircle}     label={t("kpi.lostDeals")}     value={d.lost_deals}     onClick={()=>nav("/deals")} tid="kpi-lost-deals" tint="rose" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900" style={{fontFamily:"Outfit"}}>Deals per Tahap</h3>
            <Badge variant="outline" className="text-xs">{d.active_deals + d.won_deals + d.lost_deals} total</Badge>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stageChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#064E3B" radius={[6,6,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily:"Outfit"}}>Sumber Leads</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={sources} dataKey="count" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                {sources.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5 lg:col-span-2">
          <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily:"Outfit"}}>Pendapatan Bulanan</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={d.monthly_revenue}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v)=>`${Math.round(v/1_000_000)}M`} />
              <Tooltip formatter={(v)=>formatRupiah(v)} />
              <Line type="monotone" dataKey="value" stroke="#059669" strokeWidth={3} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900" style={{fontFamily:"Outfit"}}>Follow-up Hari Ini</h3>
            <button onClick={()=>nav("/followup")} className="text-xs text-emerald-800 flex items-center gap-1 hover:underline">
              Lihat semua <ArrowUpRight className="w-3 h-3"/>
            </button>
          </div>
          <div className="space-y-3">
            {followups.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">Tidak ada follow-up hari ini.</div>}
            {followups.slice(0,5).map((f) => (
              <div key={f.id} className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50" data-testid={`dash-followup-${f.id}`}>
                <div className="w-2 h-2 mt-2 rounded-full bg-amber-500"/>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{f.title}</div>
                  <div className="text-xs text-slate-500">{f.company} · {formatDateTime(f.date)}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-5">
        <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily:"Outfit"}}>Performa Sales</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase border-b">
              <tr><th className="text-left py-2">Sales</th><th>Leads</th><th>Deals</th><th>Menang</th><th>Kalah</th><th>Konversi</th><th className="text-right pr-2">Pendapatan</th></tr>
            </thead>
            <tbody>
              {d.sales_performance.map((s) => (
                <tr key={s.user_id} className="border-b last:border-0">
                  <td className="py-2 font-medium text-slate-800">{s.name}</td>
                  <td className="text-center">{s.leads}</td>
                  <td className="text-center">{s.deals}</td>
                  <td className="text-center text-emerald-700 font-semibold">{s.won}</td>
                  <td className="text-center text-rose-600">{s.lost}</td>
                  <td className="text-center">{s.conversion}%</td>
                  <td className="text-right pr-2 font-semibold">{formatRupiah(s.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
